import React from "react";
import "../DashboardStyles.css";

export default function Chapter6Dashboard() {
  return (
    <div className="chapter chapter6">
      <div className="chapter-content">
        <p className="chapter-eyebrow">Chapter 6</p>
        <h2>Quality of Life</h2>

        <h3>6.1 Introduction</h3>
        <p>
          Economic development strategies that focus exclusively on jobs and
          investment while ignoring the quality-of-life conditions that attract
          and retain people will fail in the Nebraska Panhandle. In a region
          already experiencing population loss across nine of eleven counties,
          the decision of whether to stay, return, or move to the Panhandle is
          made not only on job availability and wages, but on the full
          experience of living here: the natural environment, the accessibility
          of essential services, the vitality of community institutions, the
          safety of neighborhoods, and the intangible sense of belonging. This
          chapter makes the case (which the action plan operationalizes) that
          quality of life is not a soft amenity separate from economic
          development. It is the precondition for it.
        </p>

        <h3>6.2 Natural Environment and Outdoor Recreation</h3>
        <p>
          The Panhandle’s most distinctive quality-of-life asset is its natural
          environment: wide-open landscapes, dramatic geological formations,
          clean air and dark skies, and access to outdoor recreation that is
          genuinely rare in the continental United States. Scotts Bluff National
          Monument, the Pine Ridge escarpment, Agate Fossil Beds, Toadstool
          Geologic Park, the Wildcat Hills, and the North Platte River corridor
          provide hiking, hunting, fishing, wildlife observation, and more, all
          within a short drive of home. Fort Robinson State Park, Nebraska’s
          largest, offers year-round recreation that most rural regions would
          envy. This environment is a resident amenity, not just a tourism draw
          — and the teen survey confirms residents recognize it: “good place to
          raise a family” was the second-most-cited reason teens gave for
          picturing a future here.
        </p>

        <h3>6.3 Healthcare Access</h3>
        <p>
          Healthcare access is the quality-of-life dimension with the starkest
          economic development implications. Anchored by Regional West Medical
          Center (Level II trauma, specialty, and surgical care serving a
          three-state region) and six Critical Access Hospitals, the system is
          uneven: residents of Banner, Sioux, Garden, and Sheridan counties (with
          whole-county primary-care HPSA designations) face limited or no local
          primary care, long drives, and a behavioral-health system so thin that
          mental-health crises often default to emergency departments. Expanding
          telehealth through BEAD, leveraging HPSA incentives, and sustaining
          the CAH network are quality-of-life imperatives as much as healthcare
          goals.
        </p>

        <h3>6.4 Education and Child Development</h3>
        <p>
          K-12 districts serve as both educational institutions and community
          anchors — often among the two or three largest employers in small
          towns. Enrollment decline driven by population loss pressures
          districts toward reduced programs, consolidation, or closure; each of
          which can accelerate community decline. Childcare availability is also
          foundational: communities with zero licensed childcare options are
          communities where working parents, particularly mothers, cannot
          participate fully in the workforce. The documented childcare gap is
          simultaneously a workforce barrier, an economic obstacle, and a
          quality-of-life deficiency — addressed under Goal 1, Objective 1.2.
        </p>

        <h3>6.5 Housing</h3>
        <p>
          New construction is not, at current rates, a plausible path to closing
          the region’s housing gap. Between 2021 and 2025, the eleven Panhandle
          counties authorized 489 new housing units. This is an average of
          roughly 90 per year against a regional stock of 38,630 units, or an
          annual addition rate of approximately 0.23 percent.
        </p>
        <p>
          Place-level data for 2025 shows how thinly that construction is
          distributed. Of twenty-six incorporated Panhandle communities in the
          permit universe, thirteen authorized no new housing at all.
          Scottsbluff, the region’s largest city at 14,373 residents, authorized
          eight units. Alliance, at 8,029 residents, authorized one.
        </p>
        <p>
          The distribution is also shifting away from municipalities.
          Unincorporated county areas, home to roughly 22 percent of the
          region’s covered population, accounted for 47 percent of all units
          authorized in 2025. The unincorporated area of Scotts Bluff County
          alone authorized more new housing than Scottsbluff and Gering
          combined. New residential construction is increasingly located outside
          municipal boundaries, where it adds no municipal tax base and places
          additional demand on county roads and rural water systems. Whatever
          constrains development inside Panhandle towns (buildable lot
          availability, water and wastewater capacity, or development cost) is
          pushing production outward rather than eliminating it.
        </p>
        <p>
          This is the empirical case for the rehabilitation and asset-inventory
          strategies in Goal 1, and for the infrastructure sequencing in Goal 3.
          A region adding ninety units a year cannot build its way past roughly
          3,261 units held off-market, and the units it does add are
          increasingly landing where they strain existing systems rather than
          strengthening town centers.
        </p>
        <p className="source-note">
          Source: U.S. Census Bureau, Building Permits Survey, county and place
          annual files, 2021–2025. Thirty-four Panhandle jurisdictions covering
          91.7 percent of the regional population participated in the survey;
          thirty-two reported all twelve months of 2025. Banner County has no
          permit-issuing jurisdiction and is excluded. Figures reflect units
          authorized by permit and exclude rehabilitation of existing units.
        </p>

        <h3>6.6 Community Character and Social Cohesion</h3>
        <figure className="ceds-figure ceds-figure--portrait">
          <img
            src="/images/ceds/bayard-grocery-2026.png"
            alt="Meat case in a small independently owned grocery store in Bayard"
            loading="lazy"
          />
          <figcaption>
            Local grocery, Bayard, 2026. Retail access in communities under
            1,500 depends on a small number of independently owned businesses,
            many held by owners approaching retirement.
          </figcaption>
        </figure>
        <p>
          One of the most consistently cited quality-of-life assets is the
          genuine sense of community that characterizes small-town life in rural
          Nebraska. Neighbors know each other, volunteers show up, and civic
          organizations knit our communities together. This social capital is
          itself an economic development asset: research on rural remote-worker
          attraction consistently finds that a sense of community belonging,
          safety, and authenticity are among the primary factors that draw and
          retain mobile workers. Sustaining it requires deliberate investment in
          gathering spaces, programming, civic leadership development, and the
          welcoming integration of new residents, particularly the region’s
          growing Hispanic community.
        </p>

        <h3>6.7 Public Safety and Emergency Services</h3>
        <p>
          Volunteer fire departments (the primary model outside
          Scottsbluff-Gering) face growing challenges recruiting and retaining
          volunteers as the working-age population declines. EMS in frontier
          counties often relies on volunteer EMTs and long response times. The
          BEAD broadband buildout has direct public-safety implications:
          reliable connectivity along county roads is safety infrastructure for
          agricultural workers, travelers, and emergency responders across a
          vast, sparsely populated landscape.
        </p>

        <h3>6.8 Quality of Life as an Economic Development Strategy</h3>
        <p>
          In the Nebraska Panhandle, quality-of-life investment and economic
          development investment are not competing uses of scarce resources.
          They are the same investment viewed from different angles. Every
          primary care physician recruited to fill an HPSA gap, every workforce
          housing unit added, and every broadband connection extended is
          simultaneously an economic development and a quality-of-life
          investment. The four goals of the Regional Action Plan are built
          around this integrated understanding.
        </p>
      </div>
    </div>
  );
}
